/**
 * Cliente da API.
 *
 * Uma porta só. Nenhuma tela chama `fetch` direto — não por elegância,
 * mas porque o tratamento de sessão expirada, de erro de conflito e de
 * escopo negado precisa ser igual em todo lugar. Espalhar `fetch` pelas
 * telas é como metade delas acaba sem tratar o 401.
 */

export class ErroApi extends Error {
  constructor(
    readonly status: number,
    readonly codigo: string,
    mensagem: string,
    readonly requestId?: string,
    readonly extra?: Record<string, unknown>,
  ) {
    super(mensagem)
    this.name = 'ErroApi'
  }

  /** O horário foi tomado enquanto a pessoa preenchia o formulário. */
  get eConflito() {
    return this.status === 409
  }

  /** Alguém editou o mesmo registro em outra aba. */
  get eDesatualizado() {
    return this.codigo === 'DESATUALIZADO'
  }
}

/**
 * Mensagens que a pessoa da recepção entende.
 *
 * A API já devolve texto em português, mas alguns códigos merecem uma
 * frase melhor no contexto da tela do que na resposta genérica.
 */
const MENSAGENS: Record<string, string> = {
  CONFLITO_HORARIO: 'Esse horário acabou de ser ocupado. Escolha outro.',
  DESATUALIZADO: 'Alguém alterou isto enquanto você editava. Recarregue para ver.',
  NAO_AUTENTICADO: 'Sua sessão expirou. Entre de novo.',
  SEM_CLINICA_ATIVA: 'Escolha uma clínica para continuar.',
  NAO_ENCONTRADO: 'Não encontramos esse registro.',
  ESTADO_TERMINAL: 'Esse atendimento já foi encerrado e não pode mudar.',
  CAMPO_OBRIGATORIO: 'Falta preencher um campo obrigatório.',
}

type Opcoes = {
  metodo?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  corpo?: unknown
  sinal?: AbortSignal
}

/** Disparado quando a sessão morre, para o app voltar ao login. */
export const SESSAO_MORTA = 'clinuma:sessao-morta'

export async function api<T>(rota: string, opcoes: Opcoes = {}): Promise<T> {
  const { metodo = 'GET', corpo, sinal } = opcoes

  const resposta = await fetch(rota, {
    method: metodo,
    // O cookie é `__Host-`, httpOnly e SameSite=strict. Nada de token
    // em localStorage: o que o JS não consegue ler, um XSS não rouba.
    credentials: 'same-origin',
    headers: corpo ? { 'Content-Type': 'application/json' } : {},
    body: corpo ? JSON.stringify(corpo) : null,
    ...(sinal ? { signal: sinal } : {}),
  })

  if (resposta.status === 204) return undefined as T

  const texto = await resposta.text()
  let dados: unknown = null
  try {
    dados = texto ? JSON.parse(texto) : null
  } catch {
    // Resposta que não é JSON já é sintoma: proxy fora do ar, HTML de
    // erro do servidor. Melhor dizer isso do que "unexpected token".
    if (!resposta.ok) {
      throw new ErroApi(resposta.status, 'RESPOSTA_INVALIDA',
        'O servidor respondeu de um jeito que não entendemos.')
    }
  }

  if (!resposta.ok) {
    const e = (dados ?? {}) as {
      code?: string; message?: string; request_id?: string
    }
    const codigo = e.code ?? 'ERRO'

    if (resposta.status === 401) {
      window.dispatchEvent(new CustomEvent(SESSAO_MORTA))
    }

    throw new ErroApi(
      resposta.status,
      codigo,
      MENSAGENS[codigo] ?? e.message ?? 'Algo deu errado. Tente de novo.',
      e.request_id,
      dados as Record<string, unknown>,
    )
  }

  return dados as T
}

/* ── tipos que as telas usam ────────────────────────────────────── */

export type Contexto = {
  usuarioId: string
  nome: string
  email: string
  /** `false` enquanto a pessoa não clicar no link do e-mail. */
  emailVerificado: boolean
  /** O profissional que esta pessoa É nesta clínica, se for algum. */
  profissionalId: string | null
  /** O carimbo dele sai completo (conselho + número + UF)? */
  podeAssinar: boolean
  tenantId: string | null
  clinica: string | null
  slug: string | null
  papel: string | null
  escopos: string[]
  clinicas: Clinica[]
}

/** Uma unidade em que a pessoa trabalha. O seletor do topo lê daqui. */
export type Clinica = { tenantId: string; nome: string; slug: string; papel: string }

/**
 * A clínica como CADASTRO — quem ela é, não em qual você está.
 *
 * `status` e `trial_ate` vêm para a tela poder avisar sobre o teste
 * acabando, mas NÃO são editáveis: a 0036 concedeu o UPDATE coluna a
 * coluna e deixou as duas de fora, porque são a chave da cobrança.
 */
export type ClinicaCadastro = {
  id: string; slug: string; nome: string; vertical: string; timezone: string
  status: string; trial_ate: string | null
  razao_social: string | null; cnpj: string | null; inscricao_municipal: string | null
  telefone: string | null; email: string | null; site: string | null
  cep: string | null; logradouro: string | null; numero: string | null
  complemento: string | null; bairro: string | null; cidade: string | null
  uf: string | null
  criado_em: string; atualizado_em: string
}

/**
 * O CATÁLOGO de pacotes — "10 sessões de fisioterapia, R$ 1.200".
 *
 * Não confundir com o pacote VENDIDO (`pacote_paciente`), que é a
 * fotografia deste no momento da compra. Reajustar aqui não reescreve
 * venda antiga, de propósito.
 */
export type PacoteCatalogo = {
  id: string
  nome: string
  procedimento_id: string | null
  sessoes: number
  valor_centavos: number | string
  validade_dias: number | null
  no_show_consome: boolean
  cancel_tardio_consome: boolean
  ativo: boolean
  criado_em: string
  /** Quantas vendas saíram deste item. Só na listagem. */
  vendidos?: number
}

/** Uma sessão do pacote vendido, no extrato. */
export type PacoteSessaoItem = {
  id: string
  ordem: number
  status: 'PREVISTA' | 'AGENDADA' | 'CONSUMIDA' | 'NO_SHOW_CONSUMIDA'
        | 'EXPIRADA' | 'ESTORNADA' | 'CANCELADA'
  consumida_em: string | null
  agendamento_id: string | null
  agendamento_inicio: string | null
  profissional: string | null
}

/** Sessão pronta para ser usada numa marcação. */
export type SessaoDisponivel = {
  pacote_sessao_id: string
  ordem: number
  pacote_pac_id: string
  nome: string
  contratadas: number
  validade: string | null
  pacote_id: string | null
  restantes: number
  procedimento_id: string | null
}

/** O roteiro de montagem, respondido pelo banco em uma consulta. */
export type Montagem = {
  passos: Array<{ id: string; pronto: boolean }>
  feitos: number
  total: number
  pacientes: number
  contagens: {
    profissionais: number; recursos: number
    procedimentos: number; jornadas: number
  }
}

export type Recurso = {
  id: string; nome: string; tipo: string; cor: string | null
  ordem: number; capacidade: number; setup_min: number; limpeza_min: number
  agendavel: boolean; ativo: boolean
}

export type Profissional = {
  id: string; nome: string; nome_curto: string | null
  conselho: string; conselho_numero: string | null; conselho_uf: string | null
  cor: string | null; ativo: boolean; especialidades: string[]
  /* ── a ficha ─────────────────────────────────────────────────────
     Campos que a agenda não usa, mas o recibo, a guia de convênio e o
     carimbo da evolução usam. */
  cpf: string | null; nascimento: string | null
  email: string | null; celular: string | null
  cbo: string | null; cns: string | null
  inicio_em: string | null; saida_em: string | null
  observacao: string | null
  /* ── as duas perguntas independentes ─────────────────────────────
     `agendavel` = aparece como coluna na agenda.
     `tem_acesso` = entra no sistema.
     Um fisioterapeuta que vem duas vezes por semana e não quer conta é
     agendável sem acesso; a recepção é o inverso. */
  agendavel: boolean; tem_acesso: boolean
  usuario_id: string | null
  usuario_nome: string | null; usuario_email: string | null
  papel: string | null; ultimo_login: string | null
}

export type Procedimento = {
  id: string; codigo: string | null; nome: string; duracao_min: number
  setup_min: number; limpeza_min: number; valor_centavos: string
  /** `NENHUMA` · `PERCENTUAL` (valor = %) · `FIXA` (valor = centavos). */
  comissao_tipo: 'NENHUMA' | 'PERCENTUAL' | 'FIXA'
  comissao_valor: number | string
  cor: string | null; ativo: boolean
}

export type Paciente = {
  id: string; nome: string; nome_social: string | null
  cpf: string | null; telefone: string | null; email: string | null
  nascimento: string | null; ativo: boolean
  financeiro: 'OK' | 'PENDENTE' | 'VENCIDO'
}

export type Alocacao = {
  recurso_id: string | null; recurso: string | null
  profissional_id: string | null; profissional: string | null
  principal: boolean
}

export type Agendamento = {
  id: string; inicio: string; fim: string; versao: number
  status: EstadoAgendamento
  confirmacao: string; origem: string
  valor_centavos: string | null; financeiro: string
  observacao: string | null
  paciente_id: string | null; paciente: string | null
  paciente_social: string | null; paciente_telefone: string | null
  paciente_financeiro: 'OK' | 'PENDENTE' | 'VENCIDO' | null
  procedimento_id: string | null; procedimento: string | null
  cor: string | null
  alocacao: Alocacao[]
}

export type Bloqueio = {
  id: string; tipo: string; titulo: string | null
  inicio: string; fim: string
  alvo_tipo: string | null; alvo_id: string | null
}

export type EstadoAgendamento =
  | 'AGENDADO' | 'CONFIRMADO' | 'CHEGOU' | 'ATENDENDO' | 'ATENDIDO'
  | 'FALTOU' | 'CANCELADO_PACIENTE' | 'CANCELADO_CLINICA' | 'REMARCADO'

export type Grade = { agendamentos: Agendamento[]; bloqueios: Bloqueio[] }

export type Lista<T> = { itens: T[]; proximo: string | null }
